// Copyright (c) Ping. All rights reserved.
//
// SEND-side mention serializer — the round-trip counterpart to
// `envelope.ts`'s `readMentions` (which decodes mentions on RECEIVE).
//
// The composer hands us the raw text the user typed plus the set of
// mention candidates (the people who can be @-mentioned in this
// conversation). We scan the text for each candidate's `@${displayName}`
// occurrence and emit the WIRE-shape mention objects that ride inside a
// `com.ping.text` envelope payload (`payload.mentions`):
//
//   { user_id: string; display_name: string; range: { start; end } }
//
// `readMentions` accepts both `user_id`/`userId` and
// `display_name`/`displayName`; we emit snake_case because that is the
// canonical on-wire form documented in the codebase memory KEY SHAPES.
//
// Matching rules:
//   * Case-insensitive (`@John` matches a candidate "john").
//   * Word-boundary-ish: the char immediately after the matched name
//     must not be a "name char" ([A-Za-z0-9_]) so "@John" does NOT
//     match candidate "Jo". The `@` itself is the left boundary.
//   * Multiple + overlapping: candidates are tried longest-displayName-
//     first so a longer name ("John Doe") wins over a shorter prefix
//     ("John"), and matches are claimed left-to-right so two distinct
//     people each get exactly one non-overlapping range.
//
// Pure, no deps.

interface MentionCandidate {
  userId: string;
  displayName: string;
}

export interface SerializedMention {
  user_id: string;
  display_name: string;
  range: { start: number; end: number };
}

const NAME_CHAR = /[A-Za-z0-9_]/;

/**
 * Scan `text` for `@${displayName}` occurrences of each candidate and
 * return the on-wire mention objects (snake_case). Returns `[]` when
 * none match. Pure.
 */
export function serializeMentions(
  text: string,
  candidates: ReadonlyArray<MentionCandidate>,
): SerializedMention[] {
  if (text.length === 0 || candidates.length === 0) return [];

  const lowerText = text.toLowerCase();

  // Longest displayName first so a longer name shadows a shorter prefix
  // (e.g. "John Doe" claims its span before bare "John" can match the
  // leading "John " inside it).
  const ordered = [...candidates]
    .filter((c) => c.displayName.length > 0 && c.userId.length > 0)
    .sort((a, b) => b.displayName.length - a.displayName.length);

  // Track which character indices are already claimed so overlapping
  // candidate names don't double-count the same span.
  const claimed: boolean[] = new Array(text.length).fill(false);
  const out: SerializedMention[] = [];

  for (const candidate of ordered) {
    const needle = `@${candidate.displayName}`.toLowerCase();
    const needleLen = needle.length;

    let from = 0;
    for (;;) {
      const idx = lowerText.indexOf(needle, from);
      if (idx === -1) break;

      const end = idx + needleLen;
      // Right word-boundary: the next char (if any) must not continue a
      // name token, otherwise "@Jo" would falsely match inside "@John".
      const nextChar = text[end];
      const rightOk = nextChar === undefined || !NAME_CHAR.test(nextChar);

      // Reject if any char in this span is already claimed by a
      // longer (earlier-processed) match.
      let free = true;
      for (let i = idx; i < end; i++) {
        if (claimed[i]) {
          free = false;
          break;
        }
      }

      if (rightOk && free) {
        for (let i = idx; i < end; i++) claimed[i] = true;
        out.push({
          user_id: candidate.userId,
          display_name: candidate.displayName,
          range: { start: idx, end },
        });
      }

      from = idx + 1;
    }
  }

  // Sort by start so consumers get mentions in reading order.
  out.sort((a, b) => a.range.start - b.range.start);
  return out;
}

/**
 * Project serialized mentions down to the bare list of userIds — the
 * shape `MessageRecord.mentions` / `Message.mentions` carry for the
 * legacy bubble pill renderer.
 */
export function mentionsToUserIds(
  serialized: ReadonlyArray<{ user_id: string }>,
): string[] {
  return serialized.map((m) => m.user_id).filter((id) => id.length > 0);
}
